import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

type ScrollLockContextType = {
  lock: (id: string) => void;
  unlock: (id: string) => void;
};

const ScrollLockContext = createContext<ScrollLockContextType | undefined>(
  undefined,
);

function ScrollLockProvider({ children }: { children: ReactNode }) {
  const [locks, setLocks] = useState<Set<string>>(() => new Set());

  const lock = useCallback((id: string) => {
    setLocks((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const unlock = useCallback((id: string) => {
    setLocks((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const locked = locks.size > 0;

  useEffect(() => {
    document.body.style.overflow = locked ? "hidden" : "";
  }, [locked]);

  const value = useMemo(() => ({ lock, unlock }), [lock, unlock]);

  return (
    <ScrollLockContext.Provider value={value}>
      {children}
    </ScrollLockContext.Provider>
  );
}

function useScrollLock(enabled: boolean) {
  const context = useContext(ScrollLockContext);
  const id = useId();

  useEffect(() => {
    if (!enabled || !context) return;
    context.lock(id);
    return () => context.unlock(id);
  }, [enabled, context, id]);
}

function useScrollLockedState(
  initialState = false,
): [boolean, (value: boolean) => void] {
  const [state, setState] = useState(initialState);
  useScrollLock(state);
  return [state, setState];
}

export { ScrollLockProvider, useScrollLock, useScrollLockedState };
